export const usePermission = () => {
  /**
   * Relative ranking of permission tiers, highest access first. Used to
   * determine whether a user's permission value for a given key meets or
   * exceeds a required minimum (e.g. "at least View").
   */
  const permissionTierRank: { [key: string]: number } = {
    Create: 4,
    Edit: 3,
    Assign: 2,
    View: 1,
    None: 0,
  };

  const meetsMinimum = (value: any, minimum: string) => {
    const currentRank = permissionTierRank[value] ?? 0;
    const requiredRank = permissionTierRank[minimum] ?? 0;
    return currentRank >= requiredRank;
  };

  /**
   * Maps a nav item's title to the permission key that gates it.
   */
  const permissionMap: { [key: string]: string } = {
    Users: "users",
    Staffing: "staffing",
    "Staff TimeClock": "staffing",
    "Booking List": "bookings",
    Events: "events",
    "Area Status": "areaStatus",
    "Matrix Displays": "matrixDisplays",
    "Gift Vouchers": "giftVouchers",
    "Discount Codes": "discountCodes",
    Products: "products",
    "POS cash": "posCash",
    POS: "dockets",
    Dockets: "dockets",
    Prep: "preps",
    Reports: "reporting",
    "Access Control": "doors",
    FAQ: "faqs",
    Location: "locations",
    Activities: "activities",
    Areas: "areas",
    Advertising: "advertising",
    Memberships: "memberships",
    "Coupon Types": "coupons",
    "Roles & Permissions": "roles",
    "Email Templates": "emailTemplates",
    Settings: "settings",
  };

  /**
   * Minimum permission tier required for a nav item to be visible.
   * Every mapped item defaults to "View"; override per title here.
   */
  const minimumPermissionMap: { [key: string]: string } = {
    POS: "Edit",
  };

  /**
   * Determines whether a nav item should be hidden from the menu.
   *
   * Items with no permission mapping are always shown. Mapped items are
   * hidden unless the user's permission for their mapped key meets the
   * required minimum tier (View by default, overridden per item in
   * `minimumPermissionMap`).
   */
  const isHidden = (item: any, permission: any): boolean => {
    const permissionKey = permissionMap[item.title];
    if (!permissionKey || !permission) return false;

    const requiredMinimum = minimumPermissionMap[item.title] || "View";
    return !meetsMinimum(permission[permissionKey], requiredMinimum);
  };

  return {
    isHidden,
    meetsMinimum,
  };
};
